import PropTypes from "prop-types"
import React, { createRef, useEffect, useState } from "react"
import { useRanger } from "react-ranger"

import styles from "./PriceSlider.module.scss"

const PriceSlider = ({ heading, slides = [] }) => {
  const sliderSteps =
    Array.isArray(slides) && slides.length > 0
      ? slides.map((slide, index) => parseInt(index))
      : []

  const initialValue = sliderSteps.length > 0 ? sliderSteps[0] : null
  const minValue = initialValue
  const maxValue =
    sliderSteps.length > 0 ? sliderSteps[sliderSteps.length - 1] : null

  const [values, setValues] = useState([initialValue])

  const currentSlide =
    sliderSteps.length > 0 && values[0]
      ? slides[values[0]]
      : slides[initialValue]

  /* */

  const handleOnDrag = (newValue) => {
    // console.log("handleOnDrag")
    setValues(newValue)
  }

  const handleOnChange = (newValue) => {
    // console.log("handleOnChange")
    setValues(newValue)
  }

  // https://github.com/akiran/react-slick/issues/1240

  const containerRef = createRef()

  useEffect(() => {
    let firstClientX = 0
    let clientX = 0

    const preventTouch = (e) => {
      const minValue = 5 // threshold

      clientX = e.touches[0].clientX - firstClientX
      // NOTE: firstClientX seems to be always zero, but we don't mind

      // Vertical scrolling does not work when you start swiping horizontally.
      if (Math.abs(clientX) > minValue) {
        e.preventDefault()
        e.returnValue = false

        return false
      }
    }

    const touchStart = (e) => {
      firstClientX = e.touches[0].clientX
    }

    const current = containerRef.current

    if (current) {
      current.addEventListener("touchstart", touchStart)
      current.addEventListener("touchmove", preventTouch, {
        passive: false,
      })
    }

    return () => {
      if (current) {
        current.removeEventListener("touchstart", touchStart)
        current.removeEventListener("touchmove", preventTouch, {
          passive: false,
        })
      }
    }
  }, [containerRef])

  const { getTrackProps, ticks, handles } = useRanger({
    values,
    onDrag: handleOnDrag,
    onChange: handleOnChange,
    min: minValue,
    max: maxValue,
    steps: sliderSteps,
    ticks: sliderSteps,
  })

  const handleTickClick = (value) => {
    setValues([value])
  }

  const sliderAtMin = values[0] === minValue || values[0] === null
  const sliderAtMax = values[0] === maxValue

  return (
    <section
      className={`${styles.element} ${sliderAtMin ? styles.sliderAtMin : ""} ${
        sliderAtMax ? styles.sliderAtMax : ""
      } text-align-left base-margin-top base-margin-bottom-large`}
    >
      {heading && <h2 className={styles.heading}>{heading}</h2>}
      <div ref={containerRef}>
        <div className={styles.track} {...getTrackProps()}>
          {ticks.map(({ value, getTickProps }) => (
            <div className={styles.tick} {...getTickProps()}>
              <button
                className={`${styles.tickButton} ${
                  values[0] === value ? styles.active : ""
                } ${value === maxValue ? styles.last : ""}`}
                onClick={() => handleTickClick(value)}
              >
                <span>{slides[value]?.value}</span>
              </button>
            </div>
          ))}
          {handles.map((handle) => {
            const { getHandleProps, value: handleValue } = handle
            return (
              <button
                className={`${styles.handleButton} ${
                  handleValue === maxValue ? styles.last : ""
                }`}
                {...getHandleProps()}
              ></button>
            )
          })}
          <div
            className={styles.progress}
            style={{
              width: `calc(${
                (values[0] / (sliderSteps.length - 1)) * 100
              }% - 20px)`,
            }}
          ></div>
        </div>
      </div>

      {currentSlide && (
        <div className={styles.currentSlide}>
          <h3>{currentSlide.description}</h3>
          <p className={styles.price}>
            <strong>{currentSlide.price} &euro;</strong>{" "}
            {currentSlide.priceDescription}
          </p>
          <p className={styles.subtext}>{currentSlide.subtext}</p>
        </div>
      )}
    </section>
  )
}

PriceSlider.propTypes = {
  slides: PropTypes.array,
}

export default PriceSlider
